import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { SchedulerDepartment } from '../../../../core/API/scheduler';
import { sortDepartmentsAlphabetically } from '../../../../module/schedule/utils/scheduleDepartmentUtils';
import styles from '../SchedulerSetting.module.scss';

export const NONE_DEPARTMENT_OPTION = { id: 0, '@id': 'none', name: 'None' } as const;

interface DepartmentSelectFieldProps {
	departments: SchedulerDepartment[];
	value: string | null;
	onChange: (departmentIri: string | null) => void;
}

const DepartmentSelectField: React.FC<DepartmentSelectFieldProps> = ({
	departments,
	value,
	onChange,
}) => {
	if (!departments.length) return null;

	const options = useMemo(
		() => [NONE_DEPARTMENT_OPTION, ...sortDepartmentsAlphabetically(departments)],
		[departments]
	);

	const selectedOption = useMemo(() => {
		if (!value) return NONE_DEPARTMENT_OPTION;
		return options.find((d) => d['@id'] === value) || NONE_DEPARTMENT_OPTION;
	}, [options, value]);

	return (
		<Autocomplete
			options={options}
			getOptionLabel={(option) => option.name}
			value={selectedOption}
			onChange={(_, option) => {
				if (!option || option['@id'] === 'none') {
					onChange(null);
					return;
				}
				onChange(option['@id']);
			}}
			isOptionEqualToValue={(option, val) => option['@id'] === val?.['@id']}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Department"
					margin="normal"
					className={styles.input}
				/>
			)}
		/>
	);
};

export default DepartmentSelectField;
